import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { ArrowRight, ChevronDown, Menu, X, Calendar } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const LOGO_URL = 'https://customer-assets-4nw71qhi.emergentagent.net/job_school-mgmt-72/artifacts/eluovc6p_ChatGPT%20Image%20Sep%2015%2C%202026%2C%2009_07_38%20AM.png';
const HERO_IMG = 'https://static.prod-images.emergentagent.com/jobs/4bff2a47-fb36-4a8a-96df-3abd338c4e14/images/441b0bf3c2f0dce8ebaeb872aabf249b7bef06a3cfc9b7abbd663c87c240abc2.jpeg';

const navLinks = [
  { label: 'Home', path: '/' },
  { label: 'About', path: '/about' },
  { label: 'Academics', path: '/academics' },
  { label: 'Admissions', path: '/admissions' },
  { label: 'Campus Life', path: '/campus-life' },
  { label: 'News & Events', path: '/news-events' },
  { label: 'Contact', path: '/contact' },
];

export default function HomePage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [nextEvent, setNextEvent] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/public/events`).then(r => {
      const today = new Date().toISOString().split('T')[0];
      const upcoming = r.data.filter(e => e.date >= today).sort((a, b) => a.date.localeCompare(b.date));
      if (upcoming.length > 0) setNextEvent(upcoming[0]);
    }).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen relative overflow-hidden" data-testid="home-page">
      {/* Warm amber background */}
      <div className="absolute inset-0 bg-[#F5D882]" />

      {/* Decorative organic shapes - scattered around the card */}
      <div className="absolute top-[-60px] left-[-40px] w-[320px] h-[320px]">
        <svg viewBox="0 0 300 300" className="w-full h-full opacity-60">
          <ellipse cx="150" cy="150" rx="130" ry="110" fill="#E8913A" />
          <path d="M80,60 Q150,20 220,80 Q200,40 140,50Z" fill="#3B7D4F" />
          <path d="M40,180 Q80,120 140,160 Q100,200 50,200Z" fill="#1B3A6B" />
        </svg>
      </div>
      <div className="absolute top-[10%] right-[-60px] w-[280px] h-[400px]">
        <svg viewBox="0 0 250 400" className="w-full h-full opacity-50">
          <path d="M120,10 Q200,80 180,200 Q160,300 80,380 Q40,300 60,200 Q80,100 120,10Z" fill="#1B3A6B" />
          <path d="M60,40 Q120,10 160,80 Q130,120 70,90Z" fill="#3B7D4F" />
          <ellipse cx="180" cy="350" rx="60" ry="40" fill="#E8913A" opacity="0.7" />
        </svg>
      </div>
      <div className="absolute bottom-[-80px] left-[15%] w-[350px] h-[300px]">
        <svg viewBox="0 0 350 300" className="w-full h-full opacity-50">
          <path d="M50,250 Q20,150 100,80 Q180,20 260,80 Q340,150 300,250Z" fill="#3B7D4F" />
          <ellipse cx="280" cy="100" rx="50" ry="70" fill="#E8913A" opacity="0.6" />
          <path d="M120,280 Q160,220 200,280Z" fill="#1B3A6B" opacity="0.5" />
        </svg>
      </div>
      <div className="absolute bottom-[5%] right-[10%] w-[200px] h-[200px]">
        <svg viewBox="0 0 200 200" className="w-full h-full opacity-40">
          <circle cx="100" cy="100" r="80" fill="#E8913A" />
          <path d="M40,80 Q80,20 140,60 Q120,100 60,100Z" fill="#3B7D4F" />
        </svg>
      </div>

      {/* Main content */}
      <div className="relative z-10 min-h-screen flex items-start justify-center px-4 sm:px-6 lg:px-10 pt-6 sm:pt-8 lg:pt-10 pb-10">
        <div className="w-full max-w-[1320px]">
          {/* The White Card - contains everything */}
          <div className="bg-white rounded-[1.5rem] sm:rounded-[2rem] lg:rounded-[2.5rem] shadow-2xl shadow-amber-900/10 overflow-hidden min-h-[85vh]" data-testid="hero-card">

            {/* Navigation - inside the card */}
            <nav className="flex items-center justify-between px-6 sm:px-8 lg:px-12 py-4 lg:py-5">
              <Link to="/" className="flex items-center gap-2" data-testid="school-logo-brand">
                <img src={LOGO_URL} alt="Tadam Academy" className="w-9 h-9 lg:w-11 lg:h-11 object-contain" />
                <span className="font-bold text-[#1B2A4A] text-sm lg:text-base tracking-tight leading-tight">Tadam<br className="sm:hidden" /> Academy</span>
              </Link>

              <div className="hidden lg:flex items-center gap-6">
                {navLinks.map(link => (
                  <Link
                    key={link.path}
                    to={link.path}
                    data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}-link`}
                    className={`text-sm font-semibold transition-colors ${
                      location.pathname === link.path
                        ? 'text-[#1B2A4A] border-b-2 border-[#E8A830] pb-0.5'
                        : 'text-slate-500 hover:text-[#1B2A4A]'
                    }`}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>

              <div className="flex items-center gap-2">
                <Link to="/login">
                  <Button variant="outline" size="sm" className="rounded-full border-slate-300 text-[#1B2A4A] font-semibold text-xs px-5 h-9 hidden sm:inline-flex" data-testid="portal-login-button">
                    Log In
                  </Button>
                </Link>
                <Link to="/admissions">
                  <Button size="sm" className="rounded-full bg-[#E8A830] hover:bg-[#D4982A] text-white font-semibold text-xs px-5 h-9 hidden sm:inline-flex" data-testid="nav-get-started-btn">
                    Get Started
                  </Button>
                </Link>
                <button className="lg:hidden p-2" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-toggle">
                  {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                </button>
              </div>
            </nav>

            {/* Mobile menu */}
            {mobileOpen && (
              <div className="lg:hidden border-t border-slate-100 bg-white animate-fade-in px-6 py-4 space-y-1">
                {navLinks.map(link => (
                  <Link key={link.path} to={link.path} onClick={() => setMobileOpen(false)}
                    className={`block px-4 py-2.5 rounded-lg text-sm font-semibold ${location.pathname === link.path ? 'text-[#E8A830] bg-amber-50' : 'text-slate-600'}`}>
                    {link.label}
                  </Link>
                ))}
                <div className="pt-3 flex gap-2">
                  <Link to="/login" onClick={() => setMobileOpen(false)} className="flex-1">
                    <Button variant="outline" className="w-full rounded-full border-slate-300 text-[#1B2A4A] text-xs font-bold">Log In</Button>
                  </Link>
                  <Link to="/admissions" onClick={() => setMobileOpen(false)} className="flex-1">
                    <Button className="w-full rounded-full bg-[#E8A830] text-white text-xs font-bold">Get Started</Button>
                  </Link>
                </div>
              </div>
            )}

            {/* Hero Content */}
            <div className="grid lg:grid-cols-2 gap-0 px-6 sm:px-8 lg:px-12 pb-8 lg:pb-0">
              {/* Left - Text + Enrollment Bar */}
              <div className="flex flex-col justify-center py-8 lg:py-16 lg:pr-8">
                <h1 className="font-display text-3xl sm:text-4xl lg:text-[3.2rem] font-black leading-[1.12] tracking-tight text-[#1B2A4A] mb-5">
                  Shape Your Child's{' '}
                  <span className="relative inline-block">
                    Future
                    <span className="absolute -bottom-1 left-0 right-0 h-3 bg-[#E8A830]/40 rounded-full -z-10" />
                  </span>{' '}
                  With Tadam Academy
                </h1>

                <p className="text-slate-500 text-sm sm:text-base leading-relaxed mb-8 max-w-lg">
                  Mwana wako mwana wangu - Your child is my child. World-class education from ECD through A-Level, nurturing excellence and character.
                </p>

                {/* Enroll Now CTA */}
                <Link to="/admissions" className="inline-block mb-10" data-testid="enrollment-bar">
                  <Button
                    size="lg"
                    className="rounded-full bg-white/30 backdrop-blur-md border border-white/50 text-[#1B2A4A] font-bold text-sm px-8 h-12 shadow-lg hover:bg-white/50 hover:shadow-xl hover:scale-105 active:scale-100 transition-all duration-300 ease-out"
                    data-testid="enroll-now-btn"
                  >
                    Enroll Now
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>

                {/* Discover More */}
                <Link to="/about" className="inline-flex items-center gap-3 text-sm text-slate-500 hover:text-[#1B2A4A] transition-colors group" data-testid="discover-more-link">
                  <span className="w-8 h-8 rounded-full bg-[#1B2A4A] flex items-center justify-center group-hover:bg-[#E8A830] transition-colors">
                    <ChevronDown className="w-4 h-4 text-white" />
                  </span>
                  <span className="w-8 h-px bg-slate-300" />
                  <span className="font-semibold">Discover More</span>
                </Link>
              </div>

              {/* Right - Illustration */}
              <div className="relative hidden lg:flex items-end justify-end">
                <div className="relative w-full max-w-[520px] -mb-0">
                  <img
                    src={HERO_IMG}
                    alt="Tadam Academy - children learning with colorful illustrations"
                    className="w-full h-auto object-contain rounded-t-3xl"
                  />
                </div>
              </div>

              {/* Mobile illustration */}
              <div className="lg:hidden flex justify-center -mx-6 sm:-mx-8">
                <img
                  src={HERO_IMG}
                  alt="Children learning"
                  className="w-full max-w-sm object-contain"
                />
              </div>
            </div>

            {/* Next Event Countdown */}
            {nextEvent && (
              <Link to="/news-events" className="block" data-testid="event-countdown-banner">
                <div className="mx-6 sm:mx-8 lg:mx-12 mb-6 bg-[#1B2A4A] rounded-xl px-5 py-3.5 flex items-center justify-between gap-4 hover:bg-[#243558] transition-colors">
                  <div className="flex items-center gap-3 min-w-0">
                    <Calendar className="w-5 h-5 text-[#E8A830] flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-white font-bold text-sm truncate">{nextEvent.title}</p>
                      <p className="text-slate-400 text-xs">{nextEvent.time || ''}</p>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-[#E8A830] font-black text-lg leading-tight">
                      {(() => {
                        const days = Math.ceil((new Date(nextEvent.date) - new Date()) / 86400000);
                        if (days <= 0) return 'Today';
                        if (days === 1) return 'Tomorrow';
                        return `${days} days`;
                      })()}
                    </p>
                    <p className="text-slate-400 text-[10px] uppercase tracking-wider">
                      {new Date(nextEvent.date).toLocaleDateString('en-ZW', { month: 'short', day: 'numeric' })}
                    </p>
                  </div>
                </div>
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
